"use client";

import { useMemo } from "react";
import { useUsersByRoleGet } from "@/hooks";
import type { GetUserBaseResponse } from "@/openapi/generated";

function formatUserLabel(user: GetUserBaseResponse): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  return name || `User ${user.userId}`;
}

export function useTaskAssigneeOptions(enabled = true) {
  const adminQuery = useUsersByRoleGet({ role: "Admin", enabled });
  const staffQuery = useUsersByRoleGet({ role: "Staff", enabled });
  const teacherQuery = useUsersByRoleGet({ role: "Teacher", enabled });

  const options = useMemo(() => {
    const merged = [
      ...adminQuery.data,
      ...staffQuery.data,
      ...teacherQuery.data,
    ];
    const byId = new Map<number, { value: number; label: string }>();
    for (const user of merged) {
      const id = Number(user.userId);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (!byId.has(id)) {
        byId.set(id, { value: id, label: formatUserLabel(user) });
      }
    }
    return [...byId.values()].sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [adminQuery.data, staffQuery.data, teacherQuery.data]);

  const isLoading =
    adminQuery.isLoading || staffQuery.isLoading || teacherQuery.isLoading;

  return { options, isLoading };
}
